import { capitalCase } from "change-case";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

export function generateAllCustomersXlsx(data: any) {
  //Arrays to separate customers into corporation and individual.
  const customersCorporation = [];
  const customersIndividual = [];
  const individualsSheetData = [];

  //Make sure all address properties are present in sequence in generated excel
  const ensureAddressProperties = (obj: any) => {
    if (!obj || typeof obj !== "object") return;

    const addressKeys = [
      "physicalAddress",
      "registeredAddress",
      "mailingAddress",
    ];

    addressKeys.forEach((addressKey) => {
      const address = obj[addressKey];
      if (address && typeof address === "object") {
        if (!address.unitNumber) address.unitNumber = "";
        if (!address.addressLine2) address.addressLine2 = "";
        if (!address.addressLine3) address.addressLine3 = "";
      }
    });
  };

  data.forEach((customer: any) => {
    ensureAddressProperties(
      customer.application?.customerDetailsCorporation ||
        customer.application?.customerDetailsIndividual,
    );
    if (customer.application?.customerDetailsCorporation?.individuals) {
      customer.application?.customerDetailsCorporation?.individuals.forEach(
        (individual: any) => {
          ensureAddressProperties(individual);
        },
      );
    }
  });

  //Map the object to a flat object and ignore individuals if needed
  const flattenObject = (
    obj: any,
    parent: string = "",
    res: any = {},
    ignoreIndividuals = false,
  ) => {
    for (const key in obj) {
      if (key === "individuals" && ignoreIndividuals) continue;

      const propName = parent ? `${parent}_${key}` : key;
      if (
        typeof obj[key] === "object" &&
        obj[key] !== null &&
        !Array.isArray(obj[key])
      ) {
        flattenObject(obj[key], propName, res, ignoreIndividuals);
      } else if (Array.isArray(obj[key])) {
        if (typeof obj[key][0] === "object") {
          obj[key].forEach((item, index) => {
            flattenObject(item, `${propName}_${index}`, res, ignoreIndividuals);
          });
        } else {
          res[capitalCase(propName)] = obj[key].join(", ");
        }
      } else {
        res[capitalCase(propName)] = obj[key];
      }
    }
    return res;
  };

  //For each object in the data, flatten it and push it to the respective array.
  data.forEach((customer: any) => {
    const flattenedCustomer = flattenObject(
      customer.application?.customerDetailsCorporation ||
        customer.application?.customerDetailsIndividual,
      "",
      {},
      customer.application?.customerDetailsCorporation?.registeredName,
    );

    if (customer.customerType === "INDIVIDUAL") {
      customersIndividual.push(flattenedCustomer);
    } else if (customer.customerType === "CORPORATION") {
      customersCorporation.push(flattenedCustomer);
      customer.application?.customerDetailsCorporation?.individuals.forEach(
        (individual: any) => {
          individualsSheetData.push({
            customerId: customer.id,
            ...flattenObject(individual, "", {}, true),
          });
        },
      );
    }
  });

  //Filter empty columns and don't show them in the excel
  const filterEmptyColumns = (dataArray: any[]) => {
    if (dataArray.length === 0) return dataArray;

    const keys = Object.keys(dataArray[0]);

    const nonEmptyKeys = keys.filter((key) =>
      dataArray.some(
        (item) =>
          item[key] !== undefined && item[key] !== "" && item[key] !== null,
      ),
    );

    return dataArray.map((item) =>
      Object.fromEntries(nonEmptyKeys.map((key) => [key, item[key]])),
    );
  };

  //Create the workbook and sheets
  const wb = XLSX.utils.book_new();

  const corporationsSheet = XLSX.utils.json_to_sheet(
    filterEmptyColumns(customersCorporation),
  );
  const customersIndividualsSheet = XLSX.utils.json_to_sheet(
    filterEmptyColumns(customersIndividual),
  );
  const individualsSheet = XLSX.utils.json_to_sheet(
    filterEmptyColumns(individualsSheetData),
  );

  //Append the sheets to the workbook
  XLSX.utils.book_append_sheet(
    wb,
    corporationsSheet,
    "Customers Type Corporation",
  );
  XLSX.utils.book_append_sheet(
    wb,
    customersIndividualsSheet,
    "Customers Type Individual",
  );
  XLSX.utils.book_append_sheet(wb, individualsSheet, "Corporation Individuals");

  //Create the blob and download the file
  const wbBlob = new Blob(
    [XLSX.write(wb, { bookType: "xlsx", type: "array" })],
    {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
  );

  saveAs(wbBlob, "izziwallet_exported_customers.xlsx");
}
